import styled from 'styled-components';
import { Card } from './surveyBaseTheme.styles';

// Form fields, option rows, and a handful of small survey-specific pieces —
// split out of surveyTheme.styles.js purely to stay under the lint's
// max-lines limit. Re-exported from surveyTheme.styles.js, so every consumer
// keeps importing from that one file.

export const FormGroup = styled.div`
  margin-bottom: 1.4rem;
`;

export const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.55rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.055em;
`;

const fieldBase = `
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(216, 184, 137, 0.26);
  border-radius: 14px;
  font-size: 0.92rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: rgba(255, 255, 255, 0.92);
  color: var(--text-primary);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--text-muted);
  }

  body.dark-mode & {
    background: var(--background-tertiary);
  }
`;

export const Input = styled.input`${fieldBase}`;
export const Select = styled.select`${fieldBase}`;
export const TextArea = styled.textarea`
  ${fieldBase}
  resize: vertical;
  min-height: 90px;
`;

export const QuestionCard = styled(Card)`
  &:hover {
    border-color: rgba(216, 184, 137, 0.4);
  }
`;

export const RequiredMark = styled.span`
  color: var(--error-color, #E53E3E);
  margin-left: 2px;
`;

// Option rows — a selectable choice (player) or an editable option (builder).
export const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(216, 184, 137, 0.22);
  border-radius: 16px;
  margin-bottom: 0.6rem;
  background: rgba(255, 255, 255, 0.78);
  min-height: 48px;
  font-size: 0.92rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
  }

  ${props => props.$checked ? `
    border-color: var(--primary-color);
    background: linear-gradient(135deg, rgba(216, 184, 137, 0.14) 0%, rgba(255, 255, 255, 0.9) 100%);
  ` : ''}

  body.dark-mode & {
    background: var(--background-tertiary);
  }
`;

export const OptionInputControl = styled.input`
  width: 20px;
  height: 20px;
  accent-color: var(--primary-color);
  flex-shrink: 0;
  cursor: pointer;
`;

export const OptionEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`;

export const AddOptionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  border: 2px dashed rgba(216, 184, 137, 0.4);
  background: var(--background-secondary);
  color: var(--text-secondary);
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.4rem;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

export const LinkChip = styled.code`
  padding: 6px 10px;
  background: var(--background-secondary);
  border: 1px solid rgba(216, 184, 137, 0.28);
  border-radius: 8px;
  word-break: break-all;
  font-size: 0.82rem;
  color: var(--text-secondary);
`;

export const BarTrack = styled.div`
  background: var(--background-secondary);
  border-radius: 999px;
  height: 10px;
  flex: 1;
  overflow: hidden;
`;

export const BarFill = styled.div`
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  height: 100%;
  border-radius: 999px;
  width: ${props => props.$pct || 0}%;
  transition: width 0.4s ease;
`;

// Matches the student quiz list's CTA buttons exactly (studentQuizCta /
// .openCta / .resultsCta in StudentQuizList.module.css) so "Take Survey" /
// "Review Responses" look like the same family as "Start Exam" / "View Results".
export const SurveyCtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: 0.005em;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${props => props.$variant === 'outline' ? `
    background: transparent;
    color: #466B5E;
    border-color: rgba(107, 142, 127, 0.4);
    &:hover:not(:disabled) {
      background: rgba(107, 142, 127, 0.08);
      border-color: #5C8676;
    }
  ` : `
    background: #3E7A82;
    color: #fff;
    &:hover:not(:disabled) {
      background: #2F6470;
    }
  `}
`;

export const IconDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--error-color);
  background: var(--background-secondary);
  color: var(--error-color);
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(229, 62, 62, 0.1);
  }
`;
